import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";
import { createAppKit } from "@reown/appkit/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "app/app";
import { chains } from "app/chains";
import { SolanaWalletProvider, ThemeProvider } from "app/shared";
import { TonConnectUIProvider } from "@tonconnect/ui-react";
import { transports } from "app/utils/transports";
import { createRoot, hydrateRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { WagmiProvider } from "wagmi";
import { injected, walletConnect } from "wagmi/connectors";
import ErrorBoundary from "./app/components/error-boundary/error-boundary";
import WalletProviderGuard from "./app/components/wallet-provider-guard/WalletProviderGuard";
import WalletConnectionErrorBoundary from "./app/components/wallet-connection-error-boundary/WalletConnectionErrorBoundary";
import GlobalMetaMaskHandler from "./app/components/global-metamask-handler/GlobalMetaMaskHandler";
import { LoggingProvider } from "./app/contexts/LoggingContext";
import LoggingErrorBoundary from "./app/components/logging-error-boundary/LoggingErrorBoundary";
import { store } from "./store";

// Initialize ethereum provider guard early
import "./app/utils/ethereum-provider-guard";

import "./styles.sass";

const queryClient = new QueryClient();

const tonManifestUrl =
  process.env.REACT_APP_TONCONNECT_MANIFEST_URL ||
  (typeof window !== "undefined"
    ? `${window.location.origin}/tonconnect-manifest.json`
    : "https://app.alphamind.co/tonconnect-manifest.json");

const projectId = "805e766b7b9e837b45ee7eb4a3865fab";

export const metadata = {
  name: "AlphaMind",
  description: "AlphaMind",
  url: "https://app.alphamind.co",
  icons: ["https://avatars.githubusercontent.com/u/37784886"],
};

const wagmiAdapter = new WagmiAdapter({
  networks: chains,
  projectId,
  connectors: [
    injected({
      shimDisconnect: true,
      unstable_shimAsyncInject: 2000,
    }),
    walletConnect({
      projectId,
      showQrModal: false,
    }),
  ],
  transports,
});

createAppKit({
  adapters: [wagmiAdapter],
  //@ts-ignore
  networks: chains,
  metadata: metadata,
  projectId,
  features: {
    analytics: true,
  },
  transports,
});

const rootElement = document.getElementById("root");

if (rootElement) {
  // Check if the content is already rendered by React Snap
  const isSnapRendered = rootElement.hasChildNodes();

  // Create the app content
  const AppContent = (
    <LoggingProvider>
      <LoggingErrorBoundary>
        <WalletProviderGuard>
          <ErrorBoundary>
            <WalletConnectionErrorBoundary>
              <WagmiProvider config={wagmiAdapter.wagmiConfig}>
                <QueryClientProvider client={queryClient}>
                  <Provider store={store}>
                    <SolanaWalletProvider>
                      <TonConnectUIProvider manifestUrl={tonManifestUrl}>
                        <ThemeProvider>
                          <App />
                          <GlobalMetaMaskHandler />
                        </ThemeProvider>
                      </TonConnectUIProvider>
                    </SolanaWalletProvider>
                  </Provider>
                </QueryClientProvider>
              </WagmiProvider>
            </WalletConnectionErrorBoundary>
          </ErrorBoundary>
        </WalletProviderGuard>
      </LoggingErrorBoundary>
    </LoggingProvider>
  );

  // Use hydrate for pre-rendered content, otherwise use render
  if (isSnapRendered) {
    // Hydrate for React Snap pre-rendered content
    hydrateRoot(rootElement, AppContent);
  } else {
    // Regular render for development
    const root = createRoot(rootElement);
    root.render(AppContent);
  }
}

// For React Snap
export default App;
